import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { getQuestionnaireAnswers } from "@medplum/core";
import { QuestionnaireResponse } from "@medplum/fhirtypes";
import { Loading, useMedplum } from "@medplum/react";

import { Group, Paper, Stack, Title } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import { WordsDiff } from "@/components/WordsDiff";
import { NewbornIPPVitalsSignQuestionsLinkIds } from "@/encounters/constants/newborn-ipp";
import { fetchAmendmentHistoryByQuestionnaireUrl } from "@/encounters/services/questionnaire-response";
import {
  formatTemperatureDisplay,
  isVitalValuePresentForDisplay,
} from "@/encounters/utils/assessments-vitals";
import { Questionnaires } from "@/fhir/enums/questionnaires";
import NotFound from "@/NotFound";

const VitalsSignDiff = ({ currentVersionIndex }: { currentVersionIndex: number }) => {
  const { encounterId } = useParams<{ encounterId: string }>();
  const medplum = useMedplum();

  const [isFetchingInitialData, setIsFetchingInitialData] = useState(true);
  const [currentVersion, setCurrentVersion] = useState<QuestionnaireResponse>();
  const [previousVersion, setPreviousVersion] = useState<QuestionnaireResponse>();

  useEffect(() => {
    const getAmendmentHistory = async () => {
      medplum.invalidateSearches("QuestionnaireResponse");
      try {
        const [foundAmendmentHistory] = await fetchAmendmentHistoryByQuestionnaireUrl(
          medplum,
          Questionnaires.NewbornIPPVitalsSign,
          encounterId!,
        );

        if (!foundAmendmentHistory) return;

        setCurrentVersion(foundAmendmentHistory[currentVersionIndex]);
        setPreviousVersion(foundAmendmentHistory[currentVersionIndex + 1]);
      } finally {
        setIsFetchingInitialData(false);
      }
    };
    getAmendmentHistory();
  }, [currentVersionIndex, encounterId, medplum]);

  if (isFetchingInitialData) return <Loading />;

  if (!currentVersion || !previousVersion) return <NotFound />;

  const previousSizeAnswers = getQuestionnaireAnswers(previousVersion);
  const currentSizeAnswers = getQuestionnaireAnswers(currentVersion);

  const previousColor =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Color]?.valueCoding?.display;
  const currentColor =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Color]?.valueCoding?.display;
  const previousHeartRate =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.HeartRate]?.valueInteger;
  const currentHeartRate =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.HeartRate]?.valueInteger;
  const previousRespiratoryRate =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.RespiratoryRate]?.valueInteger;
  const currentRespiratoryRate =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.RespiratoryRate]?.valueInteger;
  const previousTemperature =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Temperature]?.valueDecimal;
  const currentTemperature =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Temperature]?.valueDecimal;
  const previousTemperatureUnit =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.TemperatureUnit]?.valueString;
  const currentTemperatureUnit =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.TemperatureUnit]?.valueString;
  const previousNotes =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Notes]?.valueString;
  const currentNotes = currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Notes]?.valueString;
  const previousStool =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Stool]?.valueString;
  const currentStool = currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Stool]?.valueString;
  const previousVoid = previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Void]?.valueString;
  const currentVoid = currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.Void]?.valueString;
  const previousTemperatureMethod =
    previousSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.TemperatureMethod]?.valueCoding
      ?.display;
  const currentTemperatureMethod =
    currentSizeAnswers[NewbornIPPVitalsSignQuestionsLinkIds.TemperatureMethod]?.valueCoding
      ?.display;

  const previousTemperatureValue = formatTemperatureDisplay(
    previousTemperature,
    previousTemperatureUnit,
    previousTemperatureMethod,
  );
  const currentTemperatureValue = formatTemperatureDisplay(
    currentTemperature,
    currentTemperatureUnit,
    currentTemperatureMethod,
  );

  return (
    <Paper p="md" withBorder>
      <Stack mb="lg">
        <Title order={2}>Vitals</Title>
      </Stack>
      <Group grow align="stretch">
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Previous
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Heart Rate</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={
                    isVitalValuePresentForDisplay(currentHeartRate) ? `${currentHeartRate} bpm` : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousHeartRate)
                      ? `${previousHeartRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Temperature</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={currentTemperatureValue}
                  previous={previousTemperatureValue}
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Respiratory Rate</TextWrapper>
                <WordsDiff
                  showRemoved
                  current={
                    isVitalValuePresentForDisplay(currentRespiratoryRate)
                      ? `${currentRespiratoryRate} bpm`
                      : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousRespiratoryRate)
                      ? `${previousRespiratoryRate} bpm`
                      : ""
                  }
                />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Color</TextWrapper>
                <WordsDiff showRemoved current={currentColor} previous={previousColor} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Void</TextWrapper>
                <WordsDiff showRemoved current={currentVoid} previous={previousVoid} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Stool</TextWrapper>
                <WordsDiff showRemoved current={currentStool} previous={previousStool} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff showRemoved current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        <Paper p="md" withBorder>
          <Stack>
            <Title mb="lg" order={4} c="gray.6">
              Amended
            </Title>
            <Stack gap="md">
              <Stack gap="0">
                <TextWrapper fw={600}>Heart Rate</TextWrapper>
                <WordsDiff
                  current={
                    isVitalValuePresentForDisplay(currentHeartRate) ? `${currentHeartRate} bpm` : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousHeartRate)
                      ? `${previousHeartRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Temperature</TextWrapper>
                <WordsDiff current={currentTemperatureValue} previous={previousTemperatureValue} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Respiratory Rate</TextWrapper>
                <WordsDiff
                  current={
                    isVitalValuePresentForDisplay(currentRespiratoryRate)
                      ? `${currentRespiratoryRate} bpm`
                      : ""
                  }
                  previous={
                    isVitalValuePresentForDisplay(previousRespiratoryRate)
                      ? `${previousRespiratoryRate} bpm`
                      : ""
                  }
                />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Color</TextWrapper>
                <WordsDiff current={currentColor} previous={previousColor} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Void</TextWrapper>
                <WordsDiff current={currentVoid} previous={previousVoid} />
              </Stack>

              <Stack gap="0">
                <TextWrapper fw={600}>Stool</TextWrapper>
                <WordsDiff current={currentStool} previous={previousStool} />
              </Stack>
              <Stack gap="0">
                <TextWrapper fw={600}>Notes</TextWrapper>
                <WordsDiff current={currentNotes} previous={previousNotes} />
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      </Group>
    </Paper>
  );
};

export default VitalsSignDiff;
